import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';

/**
 * OTP Modal Component
 * Shows a modal dialog for OTP entry with process information
 */
const OtpModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  process = 'verification', // Type of process: 'linkedin', 'naukri', 'verification'
  platform = '', // Platform name if applicable
  message = '', // Additional message to show
  jobTitle = '' // Job title to display in the modal
}) => {
  const { isDark } = useTheme();
  const [otpValue, setOtpValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef([]);
  const { settings } = useSettings();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen && inputRefs.current[0]) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
    // Reset OTP when modal opens
    if (isOpen) {
      setOtpValue('');
      setIsSubmitting(false);
    }
  }, [isOpen, message]);

  const getTitle = () => {
    if (platform) {
      return `${platform} OTP Verification`;
    }
    switch (process) {
      case 'linkedin':
        return 'LinkedIn OTP Verification';
      case 'naukri':
        return 'Naukri OTP Verification';
      case 'sourcing':
        return 'Candidate Sourcing - OTP Required';
      case 'job_posting':
        return 'Job Posting - OTP Required';
      default:
        return 'OTP Verification Required';
    }
  };

  const getProcessIcon = () => {
    switch (process) {
      case 'linkedin':
        return '🔗';
      case 'naukri':
        return '💼';
      case 'sourcing':
        return '🔍';
      case 'job_posting':
        return '📝';
      default:
        return '🔐';
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newOtp = otpValue.split('');
    newOtp[index] = digit;
    const newOtpStr = newOtp.join('').slice(0, 6);
    setOtpValue(newOtpStr);

    // Move to next input if digit entered
    if (digit && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace - move to previous input
    if (e.key === 'Backspace' && !otpValue[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Handle Enter - submit if complete
    if (e.key === 'Enter' && otpValue.length === 6) {
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    setOtpValue(pastedData);
    // Focus appropriate input after paste
    const focusIndex = Math.min(pastedData.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = async () => {
    if (otpValue.length !== 6 || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await onSubmit(otpValue);
      // Modal will be closed by parent after successful submission
    } catch (error) {
      console.error('OTP submission error:', error);
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Escape') event.preventDefault();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className="relative z-10 w-full max-w-md mx-4"
        style={{
          backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: `2px solid ${primaryColor}30`,
        }}
      >
        {/* Gradient top border */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            borderRadius: '16px 16px 0 0',
            background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
          }}
        />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Content */}
        <div className="p-8 pt-10">
          {/* Icon and Title */}
          <div className="text-center mb-6">
            <div 
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
              style={{
                background: `linear-gradient(135deg, ${primaryColor}20, ${secondaryColor}20)`,
              }}
            >
              <span className="text-3xl" style={{ animation: 'pulse 2s infinite' }}>{getProcessIcon()}</span>
            </div>
            <h2 
              className="text-xl font-bold mb-2"
              style={{ color: isDark ? '#F9FAFB' : '#111827' }}
            >
              {getTitle()}
            </h2>
            {/* Show job title if provided */}
            {jobTitle && (
              <p 
                className="mb-2"
                style={{ 
                  color: primaryColor, 
                  fontSize: '13px',
                  fontWeight: '600',
                }}
              >
                📋 {jobTitle}
              </p>
            )}
            <p style={{ color: isDark ? '#9CA3AF' : '#6B7280', fontSize: '14px' }}>
              {message || 'Please enter the 6-digit verification code sent to your device'}
            </p>
          </div>

          {/* OTP Input Fields */}
          <div className="flex justify-center gap-2 mb-6">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={otpValue[index] || ''}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="w-12 h-14 text-center text-xl font-bold rounded-lg transition-all focus:outline-none"
                style={{
                  backgroundColor: isDark ? '#374151' : '#F3F4F6',
                  color: isDark ? '#F9FAFB' : '#111827',
                  border: otpValue[index] 
                    ? `2px solid ${primaryColor}` 
                    : `2px solid ${isDark ? '#4B5563' : '#D1D5DB'}`,
                  boxShadow: otpValue[index] ? `0 0 10px ${primaryColor}30` : 'none',
                }}
              />
            ))}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={otpValue.length !== 6 || isSubmitting}
            className="w-full py-3 px-4 rounded-lg font-semibold transition-all"
            style={{
              background: otpValue.length === 6 
                ? `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
                : isDark ? '#374151' : '#D1D5DB',
              color: otpValue.length === 6 ? '#FFFFFF' : isDark ? '#6B7280' : '#9CA3AF',
              cursor: otpValue.length === 6 && !isSubmitting ? 'pointer' : 'not-allowed',
              opacity: isSubmitting ? 0.7 : 1,
              boxShadow: otpValue.length === 6 ? `0 4px 14px ${primaryColor}40` : 'none',
            }}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Verifying...
              </div>
            ) : (
              'Verify OTP'
            )}
          </button>

          {/* Process indicator */}
          <div 
            className="mt-4 text-center text-xs"
            style={{ color: isDark ? '#6B7280' : '#9CA3AF' }}
          >
            Process: <span style={{ color: primaryColor }}>{platform || process}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtpModal;
